import { AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as EventsSvg } from "assets/icon_events.svg";
import EventsExpander from "../expander/events-expander";
import styles from "./selected-events.module.scss";
import { getRiskAnalysisEvent } from "api/layer"
import { CircularProgress, Card, Stack } from "@mui/material";
import * as turf from '@turf/turf';

let axiosObj: any = {};
function SelectedEvents() {
  const [context] = useContext(AppContext);
 const [t] = useTranslation();
  const [layerInfoList, setLayerInfoList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true)

  const handleCreateData = debounce(async (isRest = false) => {
    if (context.selectedEventsList) {
      let list = isRest ? [] : [...layerInfoList];
      list = list.map((item) => {
        return {
          ...item,
          checked: context.selectedEventsList.find((v: any) => v.id === item.id)?.checked || false
        }
      })

      context.selectedEventsList.forEach((item) => {
        let isFind = list.find((v: any) => {
          return v.id === item.id
        })
        if (isFind) {
          isFind.checked = item.checked
        } else if (item.checked) {
          let point = [context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.longitudeWgs : context.searchLocationInfo.en.location.split(",")[0], context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.latitudeWgs : context.searchLocationInfo.en.location.split(",")[1]]
          let polygon = turf.polygon([[
            [item.extent.minimumLongitude, item.extent.minimumLatitude],
            [item.extent.maximumLongitude, item.extent.minimumLatitude],
            [item.extent.maximumLongitude, item.extent.maximumLatitude],
            [item.extent.minimumLongitude, item.extent.maximumLatitude],
            [item.extent.minimumLongitude, item.extent.minimumLatitude],
          ]])

          let is = turf.booleanPointInPolygon(point, polygon);
          let noData = {}
          if (!is) {
            noData = {
              "layerId": item.layerId,
              "valueLabel": "No Data",
              "intensity": 0,
              "details": {}
            }
          }
          list.push({ ...noData, ...item })
        }
      })
      let promiseAll: any = [],
        listIndex: number[] = [];

      list.forEach((item, index) => {
        if (item.checked && !item.valueLabel && !axiosObj[item.layerId]) {
          let params = {
            longitude: context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.longitudeWgs : context.searchLocationInfo.en.location.split(",")[0],
            latitude: context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.latitudeWgs : context.searchLocationInfo.en.location.split(",")[1],
            queryLayers: item.layerId,
            country: context.currentMultipleLocationInfo ? context.currentMultipleLocationInfo.country : context.searchLocationInfo.en.country,
            oversea: context.currentMultipleLocationInfo ? true : context.searchLocationInfo.en.oversea,
            spatial: context.currentMultipleLocationInfo ? "wgs" : context.searchLocationInfo.en.spatial,
          }
          axiosObj[item.layerId] = true;
          promiseAll.push(getRiskAnalysisEvent(params))
          listIndex.push(index)
        }
      })
      if (promiseAll.length > 0) {
        await Promise.all(promiseAll).then(res => {
          res.forEach((resultItem, resultIndex) => {
            let item = resultItem.data.results[0],
              index = listIndex[resultIndex];
            delete axiosObj[list[index].layerId];
            list[index] = { ...list[index], ...item };
          })
        })
      }

      setLayerInfoList(list);
      setIsLoading(false);
    } else {
      setLayerInfoList([]);
    }
  }, 500)

  // 防抖：避免短时间内重复触发
  function debounce<T extends (...args: any[]) => void>(fn: T, delay: number, immediate = false, executeImmediately = false): (...args: Parameters<T>) => void {
    let timer: NodeJS.Timeout | null = null;
    let isInvoke = false;

    const _debounce = function (this: unknown, ...args: Parameters<T>): void {
      const executeNow = immediate && !isInvoke && executeImmediately;

      if (timer) clearTimeout(timer);

      if (executeNow) {
        fn.apply(this, args);
        isInvoke = true;
      } else {
        timer = setTimeout(() => {
          fn.apply(this, args);
          isInvoke = false;
        }, delay);
      }
    };

    return _debounce;
  }

  useEffect(() => {
    setIsLoading(true);
    handleCreateData()
  }, [context.selectedEventsList])

  useEffect(() => {
    setIsLoading(true);
    handleCreateData(true);
    axiosObj = {};
  }, [context.currentMultipleLocationInfo, context.searchLocationInfo])

  return (<div className={styles.layers}>
    <div className={styles.header}>
      <div className={styles.icon}>
        <EventsSvg />
      </div>
      <div>{t("events.Selected Events")}</div>
      {context.selectedEventsList.length > 0 && <div className={styles.count}>
        {t("events.eventTotal", { num: context.selectedEventsList.length })}
      </div>}
    </div>
    {context.selectedEventsList.length > 0 && <div>
      {isLoading ?
        (<div className="flex flex-col items-center justify-center py-2">
          <CircularProgress color="success" className="my-3" />
          <div>{t("single.loadingLayerAnalyses")}</div>
        </div>) :
        (<>
          {layerInfoList.map((item) => (item.checked && (
            <Card
              variant="outlined"
              style={{
                borderRadius: "4px",
                marginTop: "15px",
                marginBottom: "15px",
                marginLeft: "10px",
                border: "1px solid var(--Surface-Border---Outline, rgba(33, 33, 33, .12))",
                background: "#fff",
                boxShadow: "0 1px 6px #0000001f, 0 1px 4px #0000001f",
              }}
              key={item.layerId}
            >
              <EventsExpander layerInfo={item}>
                {(item.details && JSON.stringify(item.details) !== "{}") &&
                  (<Stack spacing={0.3} className={styles.detail}>
                    {Object.keys(item.details).reverse().map(e => {
                      return (
                        <Stack key={e} direction="row" spacing={2}>
                          <div className={styles.key}>
                            {t("legend." + e).startsWith("legend.") ? e : t("legend." + e)}:
                          </div>
                          <div className={styles.value}>
                            {t("legend." + item.details[e]).startsWith("legend.")
                              ? item.details[e]
                              : t("legend." + item.details[e])}
                          </div>
                        </Stack>
                      );
                    })}
                  </Stack>)}
              </EventsExpander>
            </Card>
          )))}
        </>)}
    </div>}
  </div >);
}

export default SelectedEvents;

